import {
	normaliseSignatureTokens,
	placeEmailSignature,
	SIGNATURE_TOKEN,
} from "./emailSignaturePreview.util";
import { isNormalizedCompanyNameKey, resolveNormalizedCompanyName } from "./normalizedCompanyName";

import type { ListPerson } from "../../../schema/list";

import type { SenderContext } from "./resolveSenderContext.util";

/**
 * Matches a template token `{key}` or `{key|label}`. `key` is captured and must
 * not contain `{`, `}` or `|`; the optional `|label` part is consumed but
 * ignored (the label is purely a display hint inserted by the editors).
 */
const TOKEN_REGEX = /\{([^{}|]+)(?:\|[^{}]*)?\}/g;

/** Escapes the five HTML-significant characters so user/prospect text cannot inject markup. */
export const escapeHtml = (value: string): string =>
	value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

const unsubscribeChipHtml = (label: string): string =>
	`<span data-testid="unsubscribe-chip" class="inline-flex items-center rounded-full border border-blue-action/20 bg-blue-action/10 px-1.5 py-0.5 text-xs font-medium text-blue-action">${escapeHtml(
		label
	)}</span>`;

/**
 * Walks a `{prospect_*}` / `{column_<id>_*}` key against the lead, mirroring the
 * resolution logic in `replacePlaceholders`. Returns `undefined` when the value
 * cannot be resolved.
 */
const resolveLeadValue = (lead: ListPerson | undefined, key: string): unknown => {
	if (!lead) {
		return undefined;
	}

	const objectPath = key.split("_");
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let realValue: any = lead;

	for (const path of objectPath) {
		if (path === "column") {
			const columnValue = lead.columns?.find((c) => c.columnConfigId === +objectPath[1])?.value;

			realValue =
				columnValue && (columnValue as { answer?: unknown }).answer
					? (columnValue as { answer?: unknown }).answer
					: columnValue;
			break;
		}

		if (!realValue) {
			break;
		}

		realValue = Array.isArray(realValue[path]) ? realValue[path][0] : realValue[path];
	}

	return realValue;
};

export interface ResolveDraftPreviewOptions {
	isLinkedin?: boolean;
	/** The draft's Include signature choice: append the signature when the body places none. */
	includeSignature?: boolean;
}

/**
 * Builds the safe HTML shown in the draft preview, resolving template variables
 * to the data that will actually be sent:
 * - prospect/column tokens → escaped real values from the lead,
 * - the selected sender's signature HTML (trusted, un-escaped), placed by `placeEmailSignature`
 *   after every other token, as the send places it: at a `{signature}` token, once, or below the
 *   body when the draft's Include signature is on (PREC-4401),
 * - `{senderFirstName}`/`{senderLastName}` → escaped sender values,
 * - `{unsubscribe_link}` → a styled chip (the real URL is generated server-side).
 *
 * LinkedIn drafts strip `{signature}`/`{unsubscribe_link}` since they do not
 * apply there. Unknown tokens are left literal so typos stay visible. The plain
 * text body is HTML-escaped first (the only un-escaped injection is the trusted
 * signature HTML), making the result safe for `dangerouslySetInnerHTML`.
 */
export const resolveDraftPreviewHtml = (
	content: string,
	lead: ListPerson | undefined,
	suggestions: string[],
	senderContext: SenderContext,
	unsubscribeLabel: string,
	options: ResolveDraftPreviewOptions = {}
): string => {
	if (!content) {
		return "";
	}

	const { isLinkedin = false, includeSignature = false } = options;

	const escaped = normaliseSignatureTokens(escapeHtml(content).replace(/\n/g, "<br>"));

	const filled = escaped.replace(TOKEN_REGEX, (match: string, rawKey: string) => {
		const key = rawKey.trim();

		switch (key) {
			case "signature":
				return SIGNATURE_TOKEN;
			case "unsubscribe_link":
				return isLinkedin ? "" : unsubscribeChipHtml(unsubscribeLabel);
			case "senderFirstName":
				return senderContext.senderFirstName ? escapeHtml(senderContext.senderFirstName) : "";
			case "senderLastName":
				return senderContext.senderLastName ? escapeHtml(senderContext.senderLastName) : "";
		}

		if (!suggestions.includes(key)) {
			return match;
		}

		const resolved = resolveLeadValue(lead, key);

		// The normalized company name is null until something resolves it; the send falls back to the
		// raw company name there, so the preview shows the same rather than a gap (PREC-3923).
		if (isNormalizedCompanyNameKey(key)) {
			return escapeHtml(resolveNormalizedCompanyName(resolved, lead));
		}

		if (resolved === undefined || resolved === null || resolved === "") {
			return "";
		}

		return escapeHtml(String(resolved));
	});

	return isLinkedin
		? filled.replaceAll(SIGNATURE_TOKEN, "")
		: placeEmailSignature(filled, senderContext.signature, includeSignature);
};
